import { showDialog } from "@widgets/CommonDialog";
import { ApiError, ApiResponse } from "@shared/type/ApiType";
import { global } from "@shared/libs/Global";

// BE 컨텍스트 경로(로컬 dev는 setupProxy, 운영은 nginx 리버스 프록시 사용)
const CONTEXT_PATH = "/snotes";

/**
 * 모든 요청에 붙는 공통 헤더.
 *
 * axios 계열(restApi/CommonUtils)은 호출마다 X-TENANT-ID를 직접 넣고 있는데
 * apiFetch에는 그게 없어서, 이 헤더 없이 나간 요청이 백엔드에서 테넌트를 정하지
 * 못해 "접근권한이 없습니다"로 막혔다. 호출자가 빠뜨릴 수 있는 값이므로
 * 여기서 한 번에 붙인다. (로그인 전이면 userInfo가 비어 있어 DEFAULT)
 */
function commonHeaders(): Record<string, string> {
  return {
    "X-TENANT-ID": global.getState().userInfo?.tenant_id || "DEFAULT",
  };
}

/**
 * fetch 기반 wrapper object
 * throw 포함
 */
export async function apiFetch<T = any>(
  endpoint: string,
  options: RequestInit = {}
): Promise<ApiResponse<T>> {
  const init: RequestInit = { credentials: "include", ...options };
  if (init.body && !isFormData(init.body) && typeof init.body === "object") {
    init.body = JSON.stringify(init.body);
    init.headers = {
      Accept: "application/json",
      "Content-Type": "application/json",
      ...commonHeaders(),
      // 호출자가 명시한 헤더가 항상 이긴다 (공통값을 덮어쓸 수 있게)
      ...(options.headers || {}),
    };
  } else {
    init.headers = {
      Accept: "application/json",
      ...commonHeaders(),
      ...(options.headers || {}),
    };
  }

  try {
    const response = await fetch(`${CONTEXT_PATH}${endpoint}`, init);
    const json = await parseJson<any>(response);

    if (!response.ok) {
      throw new Error(JSON.stringify(json));
    }

    assertApiResponse(json);
    return json as ApiResponse<T>;
  } catch (error) {
    console.error("API EXCEPTION", error);

    /* assertApiResponse 가 던진 ApiError 는 message 가 JSON 이 아니다 — 아래 JSON.parse 에 넣으면
       "Unexpected token 'i', "invalid re"…" 로 뜻 모를 오류가 된다(2026-09-30). 그대로 올린다 */
    if (error instanceof ApiError) {
      throw error;
    }

    if (error instanceof Error) {
      const json = JSON.parse(error.message);

      const status = json?.status ?? 'E';
      const code = json?.code ?? 500;
      const message = json?.message ?? '서버 오류가 발생하였습니다.';
      const errorDetail = json?.error ?? makeError('SERVER_ERROR');

      if ([498].includes(code)) {
        // 세션 만료 시 현재 경로를 저장하고 로그인으로 이동
        await showDialog("warning", "세션이 만료되었습니다.");
        if (window.location.pathname !== "/login") {
          if (typeof (window as any).__removeBeforeUnload__ === "function") {
            (window as any).__removeBeforeUnload__();
          }
          const path = window.location.pathname;
          if (path.startsWith("/menu/") || path.startsWith("/share/")) {
            sessionStorage.setItem(
              "return_path",
              path + window.location.search + window.location.hash
            );
          }
          // 세션 만료 시점엔 로그인 상태였으므로 스토어의 tenant_id가 유효하다.
          // tenant를 붙이지 않으면 로그인 화면이 DEFAULT 테넌트로 떨어져 테넌트가 풀린다.
          // 단 DEFAULT는 붙이지 않는다 — 로그인 화면이 ?tenant=DEFAULT를 모르는 테넌트로
          // 오탐해서 파라미터를 떼며 전체 리로드가 한 번 더 발생한다.
          const tenantId =
            global.getState().userInfo?.tenant_id || "DEFAULT";
          window.location.replace(
            tenantId === "DEFAULT" ? "/login" : "/login?tenant=" + tenantId
          );
          return json as ApiResponse<T>;
        }
      }

      throw new ApiError(status, code, message, errorDetail);
    } else {
      throw ApiError.serverError();
    }
  }
}

export function makeError(errorCode: string) {
  const now = new Date();
  const pad = (n: number) => String(n).padStart(2, "0");

  const year = now.getFullYear();
  const month = pad(now.getMonth() + 1);
  const day = pad(now.getDate());
  const hour = pad(now.getHours());
  const minute = pad(now.getMinutes());
  const second = pad(now.getSeconds());
  const ms = String(now.getMilliseconds()).padStart(3, "0");

  const errorTime = `${year}-${month}-${day} ${hour}:${minute}:${second}.${ms}`;

  return {
    errorCode,
    errorTime,
  };
}

const isPlainObject = (v: unknown): v is Record<string, unknown> =>
  v !== null && typeof v === 'object' && Object.prototype.toString.call(v) === '[object Object]';

export function assertApiResponse(raw: unknown): asserts raw is ApiResponse<unknown> {
  if (!isPlainObject(raw)) {
    console.error('invalid response format')
    throw ApiError.serverError('invalid response format', makeError('INVALID_FORMAT'));
  }

  const status = raw.status;
  if (status !== 'S' && status !== 'E' && status !== 'W' && status !== 'F') {
    console.error('invalid response (status)')
    throw ApiError.serverError('invalid response (status)', makeError('INVALID_STATUS'));
  }

  if (typeof raw.code !== 'number' || Number.isNaN(raw.code)) {
    console.error('invalid response (code)')
    throw ApiError.serverError('invalid response (code)', makeError('INVALID_CODE'));
  }

  if (typeof raw.message !== 'string') {
    console.error('invalid response (message)')
    throw ApiError.serverError('invalid response (message)', makeError('INVALID_MESSAGE'));
  }
}

function isFormData(body: unknown): body is FormData {
  return typeof FormData !== "undefined" && body instanceof FormData;
}

async function parseJson<T = any>(res: Response): Promise<T | null> {
  try {
    return await res.json();
  } catch {
    return null;
  }
}

export function toApiResponse(err: unknown): ApiResponse<never> {
  if (err instanceof ApiError) {
    return {
      status: err.status,
      code: err.code,
      message: err.message,
      data: null,
      error: err.error ?? null,
    };
  }
  return {
    status: "E",
    code: 500,
    message: "서버 오류가 발생하였습니다.",
    data: null,
    error: makeError?.("SERVER_ERROR") ?? null,
  };
}
